import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { useAuth } from '@shared/auth/authContext'
import { ModerationErrorKind } from '../../lib/moderationError'
import {
  isNotificationNew,
  loadReadIds,
  matchFingerprints,
  mergeReadIds,
  READ_IDS_LIMIT,
  readIdsStorageKey,
  saveReadIds,
  selectToMarkRead,
} from '../../lib/readState'
import type { Notification, NotificationStatus } from '../types'
import { NotificationsUiContext, type ModerationIssue } from './NotificationsUiContext'

export function NotificationsUiProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth()
  const profileId = user?.profile.id
  const userId = typeof profileId === 'number' ? profileId : null
  const role = user?.profile.role ?? null

  const [moderationIssues, setModerationIssues] = useState<Record<number, ModerationIssue>>({})
  const moderationIssuesRef = useRef(moderationIssues)
  useEffect(() => {
    moderationIssuesRef.current = moderationIssues
  }, [moderationIssues])
  const [open, setOpenState] = useState(false)
  const openRef = useRef(false)
  const [notifications, setNotificationsState] = useState<Notification[]>([])
  const notificationsRef = useRef<Notification[]>([])

  // Прочитанность: id из localStorage (по пользователю) и отпечатки прочитанных живых
  // пушей (id < 0) — только в памяти, пока снимок не принесёт настоящую строку.
  const [readStore, setReadStore] = useState(() => ({
    role,
    userId,
    ids: loadReadIds(role, userId),
    fingerprints: [] as string[],
  }))
  let store = readStore
  if (readStore.userId !== userId || readStore.role !== role) {
    store = { role, userId, ids: loadReadIds(role, userId), fingerprints: [] }
    setReadStore(store)
  }
  const storeRef = useRef(store)
  useEffect(() => {
    storeRef.current = store
    notificationsRef.current = notifications
  }, [store, notifications])

  // Другая вкладка отметила прочитанное — подхватываем без перезагрузки.
  useEffect(() => {
    if (userId == null || role == null) return
    const key = readIdsStorageKey(role, userId)
    const onStorage = (e: StorageEvent) => {
      if (e.key !== key && e.key !== null) return
      const ids = loadReadIds(role, userId)
      setReadStore((prev) => {
        if (prev.role !== role || prev.userId !== userId) return prev
        const next = { ...prev, ids: mergeReadIds(prev.ids, ids) }
        storeRef.current = next
        return next
      })
    }
    window.addEventListener('storage', onStorage)
    return () => window.removeEventListener('storage', onStorage)
  }, [role, userId])

  /** Запоминает прочитанное. Хранилище не приняло запись — ничего не меняем: всё остаётся новым. */
  const commitRead = useCallback(
    (addIds: number[], addFingerprints: string[], consumedFingerprints: string[] = []) => {
      if (addIds.length === 0 && addFingerprints.length === 0) return
      const cur = storeRef.current
      // Сливаем со свежим содержимым хранилища: другая вкладка могла дописать id после нашего чтения.
      const ids = mergeReadIds(mergeReadIds(loadReadIds(cur.role, cur.userId), cur.ids), addIds)
      if (!saveReadIds(cur.role, cur.userId, ids)) return
      // Отпечатки — со счётом: один прочитанный пуш закрывает одну строку снимка.
      const fingerprints = [...cur.fingerprints]
      for (const fp of consumedFingerprints) {
        const at = fingerprints.indexOf(fp)
        if (at >= 0) fingerprints.splice(at, 1)
      }
      const next = {
        role: cur.role,
        userId: cur.userId,
        ids,
        fingerprints: [...fingerprints, ...addFingerprints].slice(-READ_IDS_LIMIT),
      }
      storeRef.current = next
      setReadStore(next)
    },
    []
  )

  // Закрытие окна (open true → false) отмечает прочитанным всё, что в нём было,
  // кроме нерешённых заявок.
  const setOpen = useCallback(
    (next: boolean) => {
      if (openRef.current && !next) {
        const resolvedIds = new Set(
          Object.entries(moderationIssuesRef.current)
            .filter(([, issue]) => issue === ModerationErrorKind.resolved)
            .map(([id]) => Number(id))
        )
        const { ids, fingerprints } = selectToMarkRead(notificationsRef.current, resolvedIds)
        commitRead(ids, fingerprints)
      }
      openRef.current = next
      setOpenState(next)
    },
    [commitRead]
  )

  const readIdSet = useMemo(() => new Set(store.ids), [store.ids])
  const readFingerprintSet = useMemo(() => new Set(store.fingerprints), [store.fingerprints])
  const isNew = useCallback(
    (n: Notification) => isNotificationNew(n, readIdSet, readFingerprintSet),
    [readIdSet, readFingerprintSet]
  )
  const newNotifications = useMemo(() => notifications.filter(isNew), [notifications, isNew])

  const setModerationIssue = useCallback((id: number, issue: ModerationIssue) => {
    setModerationIssues((prev) => (prev[id] === issue ? prev : { ...prev, [id]: issue }))
  }, [])

  // After approve/decline, the backend can keep echoing the same notification
  // through the WS `latest_notifications` snapshot for a few seconds. Without
  // this guard, the badge count bounces back up the moment a new snapshot
  // arrives. Keep dismissed IDs in a ref so they survive across WS pushes.
  const dismissedIds = useRef<Set<number>>(new Set())

  // Решённая (одобренная/отклонённая) заявка получает статус и уходит во вкладку «Прочитанное»
  // вместо удаления. Статус хранится в ref, чтобы пережить каждый новый снимок WS и
  // заново на него накладываться, иначе карточка вернулась бы в исходное «действуемое» состояние.
  const resolvedStatuses = useRef<Map<number, NotificationStatus>>(new Map())

  const applyStatus = useCallback((n: Notification): Notification => {
    const status = resolvedStatuses.current.get(n.id)
    return status ? { ...n, localStatus: status, actionable: false } : n
  }, [])

  const setNotifications = useCallback(
    (list: Notification[]) => {
      const incoming = list.filter((n) => !dismissedIds.current.has(n.id)).map(applyStatus)
      const incomingIds = new Set(incoming.map((n) => n.id))
      // Настоящая строка с отпечатком прочитанного живого пуша — прочитана, id запоминаем.
      const matched = matchFingerprints(
        incoming,
        storeRef.current.fingerprints,
        new Set(storeRef.current.ids)
      )
      commitRead(matched.ids, [], matched.consumed)
      setNotificationsState((prev) => {
        // Keep resolved cards the snapshot no longer includes so their outcome
        // stays visible (in the «read» tab after the panel is closed) until reload.
        const retained = prev.filter((n) => n.localStatus && !incomingIds.has(n.id))
        return [...incoming, ...retained]
      })
    },
    [applyStatus, commitRead]
  )

  const addNotification = useCallback(
    (n: Notification) => {
      if (dismissedIds.current.has(n.id)) return
      const item = applyStatus(n)
      setNotificationsState((prev) =>
        prev.some((x) => x.id === n.id)
          ? prev.map((x) => (x.id === n.id ? item : x))
          : [item, ...prev]
      )
    },
    [applyStatus]
  )

  const clearModerationIssue = useCallback((id: number) => {
    setModerationIssues((prev) => {
      if (!(id in prev)) return prev
      const rest = { ...prev }
      delete rest[id]
      return rest
    })
  }, [])

  const removeNotification = useCallback(
    (id: number) => {
      dismissedIds.current.add(id)
      clearModerationIssue(id)
      setNotificationsState((prev) => prev.filter((n) => n.id !== id))
    },
    [clearModerationIssue]
  )

  const resolveNotification = useCallback(
    (id: number, status: NotificationStatus) => {
      resolvedStatuses.current.set(id, status)
      commitRead([id], [])
      clearModerationIssue(id)
      setNotificationsState((prev) =>
        prev.map((n) => (n.id === id ? { ...n, localStatus: status, actionable: false } : n))
      )
    },
    [clearModerationIssue, commitRead]
  )

  const value = useMemo(
    () => ({
      open,
      setOpen,
      notifications,
      setNotifications,
      addNotification,
      removeNotification,
      resolveNotification,
      isNew,
      newNotifications,
      moderationIssues,
      setModerationIssue,
    }),
    [
      open,
      setOpen,
      notifications,
      setNotifications,
      addNotification,
      removeNotification,
      resolveNotification,
      isNew,
      newNotifications,
      moderationIssues,
      setModerationIssue,
    ]
  )

  return <NotificationsUiContext.Provider value={value}>{children}</NotificationsUiContext.Provider>
}
